<template>
  <div class="sharedMapMenu">
    <div
      data-testid="header"
      class="padded header"
    >
      <input
        v-if="isEditableMap"
        v-model="mapName"
        class="inputMapName menu-title outlined-input"
        type="text"
        placeholder="Enter Map Name"
        contenteditable
      >
      <p
        v-else
        class="menu-title"
      >
        {{ mapName }}
      </p>

      <SaveStatus
        :saved="sharedMap.editedOn ? new Date(sharedMap.editedOn.timestamp) : null"
        :username="sharedMap.editedOn?.name || ''"
      />
      <div
        v-if="isEditableMap"
        class="button-group"
      >
        <AddFeatureButton
          class="addFeatureButton"
          :allow-drivetime-polygons="true"
          @addNewFeature="handleAddNewFeature"
        />
        <v-menu :close-on-content-click="false">
          <template #activator="{props, isActive}">
            <np-action-btn
              :append-icon="isActive ? 'mdi-chevron-up' : 'mdi-chevron-down'"
              v-bind="props"
            >
              Data Actions
            </np-action-btn>
          </template>
          <v-list>
            <UpsertSharedMap
              :operations="['add']"
              :collections="['features']"
            >
              <template #button="{ disabled, onClick }">
                <ButtonDropdownOption
                  :disabled="disabled"
                  title="Add to Map"
                  @handle-click="onClick"
                />
              </template>
            </UpsertSharedMap>
            <RefreshFeatures />
          </v-list>
        </v-menu>
      </div>
    </div>
    <hr class="spacer">
    <div data-testid="body">
      <LoadIcon
        v-if="folderLoading"
        class="loading"
        :size="24"
      />
      <FeatureFolder
        v-for="(item, index) in sharedMap.folders"
        v-else
        :key="item.folderID ?? item.id"
        :folder-index="[index]"
        :overlay="sharedMap"
        :folder-item="item"
        :can-edit="isEditableMap"
        @setFolderLoading="setFolderLoading"
      />
    </div>
  </div>
</template>

<script lang="ts">
// Services and Helpers
import {mapState} from 'vuex';
import {getCollection} from '@/lib/filters/filters.js';
import {useMenuClickListener} from '@/mixins';
import type {SharedMap} from '@/lib/classes/SharedMap';

// Components
import AddFeatureButton from '@/components/FeatureMenuItems/AddFeatureButton.vue';
import FeatureFolder from '@/components/FeatureMenuItems/Folders/FeatureFolder.vue';
import LoadIcon from '@/components/svgs/LoadIcon.vue';
import SaveStatus from '@/components/Reusable/SaveStatus.vue';
import UpsertSharedMap from '@/internal/components/FilterMenu/sections/UpsertSharedMap/UpsertSharedMap.vue';
import ButtonDropdownOption from '@/components/Reusable/Buttons/ButtonDropdownOption.vue';
import RefreshFeatures from '@/external/components/RefreshFeatures/RefreshFeatures.vue';

let timer: ReturnType<typeof setTimeout>;

export default {
  name: 'SharedItemMenu',
  components: {
    AddFeatureButton,
    LoadIcon,
    SaveStatus,
    FeatureFolder,
    UpsertSharedMap,
    ButtonDropdownOption,
    RefreshFeatures,
  },
  mixins: [useMenuClickListener('handleMenuClick')],
  provide() {
    return {
      useLocalListeners: true,
    };
  },
  data() {
    return {
      folderLoading: false,
      sharedMapName: '',
      cancelCallback: null as Function | null,
      showDialog: false,
    };
  },
  computed: {
    ...mapState({
      sharedMap: (state: any) => state.sharedMap as SharedMap,
      isEditableMap: (state: any) => state.isEditableMap as boolean,
    }),
    mapName: {
      get() {
        return this.sharedMapName;
      },
      set(value: string) {
        this.sharedMapName = value;
        const updateName = this.debounce(async () => {
          await this.sharedMap.update({name: value});
        }, 1250);
        updateName();
      },
    },
  },
  mounted() {
    this.sharedMapName = this.sharedMap.name;
    getCollection('features').subscribe(null, () => {
      // sharedMap.features is not reactive to changes made via its own class methods, so we have to watch for,
      // changes to the features collection to trigger a re-render
      this.$forceUpdate();
      this.folderLoading = false;
    });
  },
  beforeUnmount() {
    if (this.cancelCallback instanceof Function) {
      this.cancelCallback();
      this.cancelCallback = null;
    }
  },
  methods: {
    debounce(func: Function, timeout = 250) {
      return (...args: any[]) => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          func.apply(this, args);
        }, timeout);
      };
    },
    async handleAddNewFeature(createNewFeature: (parent: SharedMap, finishedCallback: Function) => Promise<Function>) {
      // Cancel all existing feature listeners
      if (this.cancelCallback instanceof Function) {
        this.cancelCallback();
        this.cancelCallback = null;
      }
      // add context of the current overlay and the finished callback to the new feature function
      this.cancelCallback = await createNewFeature(this.sharedMap, () => this.cancelCallback = null);
    },
    setFolderLoading(loading: boolean) {
      this.folderLoading = loading;
    },
    handleMenuClick() {
      if (this.cancelCallback instanceof Function) {
        this.cancelCallback();
        this.cancelCallback = null;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.button-group {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 5px;
  width: 95%;
}

.spacer {
  padding-bottom: 0px;
  border-top: 1px solid #ccc;
  background: none;
}

.sharedMapMenu {
  padding: 0 !important;
}

.padded {
  padding: 25px 25px 0;
}

.header {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 15px;
  margin-bottom: 20px;
}

.inputMapName {
  height: 34px;
  width: 95%;
  margin: 10px 0 5px 0;
  font-size: 20px;
  padding: 6px 6px 6px 9.5px;
  white-space: nowrap;
  overflow: hidden;
  border-style: solid;
  border-width: 1px;
  border-radius: 4px;
  border-color: main.$npOrange;
  color: main.$npGray;
  &::placeholder {
    color: #aaa;
  }
}


</style>
